import jsPDF from 'jspdf';
import { BasePDFOptions } from '..';
import { RevenueReportItem } from '@/types/pos/revenue-report';
import { capitalizeFirstLetter, formatToPKR } from '@/lib/helper';
import { format } from 'date-fns';

export async function revenueReportTemplate(
  options: BasePDFOptions<RevenueReportItem[]>
): Promise<void> {
  const {
    data,
    filename = 'revenue-report.pdf',
    title = 'Revenue Report',
    orientation = 'portrait',
    summary
  } = options;

  const pdf = new jsPDF(orientation === 'landscape' ? 'l' : 'p', 'mm', 'a4');
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 10;
  let yPosition = margin;

  // Header
  pdf.setFillColor(30, 41, 59);
  pdf.rect(0, 0, pageWidth, 25, 'F');
  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(20);
  pdf.setFont('helvetica', 'bold');
  pdf.text(title, margin, 15);

  pdf.setFontSize(8);
  pdf.setFont('helvetica', 'normal');
  const currentDate = new Date();
  const formattedDate = format(currentDate, "dd MMM yyyy");
  const formattedTime = format(currentDate, "hh:mm a");
  pdf.text(`Generated: ${formattedDate} at ${formattedTime}`, margin, 21);

  const totalRecordsText = `Total Records: ${summary?.totalCount || data.length}`;
  pdf.text(totalRecordsText, pageWidth - margin, 21, { align: 'right' });

  yPosition = 35;

  // Table header
  pdf.setTextColor(15, 23, 42);
  const headers = ['Txn #', 'Date', 'Member', 'Type', 'Item', 'Total', 'Discount'];
  
  const usableWidth = pageWidth - (margin * 2);
  const colWidths = [
    usableWidth * 0.10, // Txn #
    usableWidth * 0.10, // Date
    usableWidth * 0.18, // Member
    usableWidth * 0.12, // Type
    usableWidth * 0.25, // Item
    usableWidth * 0.13, // Total
    usableWidth * 0.10, // Disc.
  ];
  const tableStartX = margin;

  const drawHeader = (y: number) => {
    pdf.setFillColor(241, 245, 249);
    pdf.rect(tableStartX, y, usableWidth, 8, 'F');
    pdf.setDrawColor(203, 213, 225);
    pdf.setLineWidth(0.5);
    pdf.line(tableStartX, y + 8, tableStartX + usableWidth, y + 8);

    pdf.setTextColor(51, 65, 85);
    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'bold');
    
    let x = tableStartX;
    headers.forEach((header, index) => {
      const padding = 2;
      if (index >= 5) {
         pdf.text(header, x + colWidths[index] - padding, y + 5, { align: 'right' });
      } else {
         pdf.text(header, x + padding, y + 5);
      }
      x += colWidths[index];
    });
  };

  drawHeader(yPosition);
  yPosition += 8;

  // Rows
  pdf.setFontSize(7);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(15, 23, 42);
  const lineHeight = 4;
  const paddingY = 2;

  data.forEach((row, rowIndex) => {
    const rowData = [
      row.transaction_number,
      row.created_at ? format(new Date(row.created_at), "dd/MM/yy") : 'N/A',
      row.member_name || '',
      capitalizeFirstLetter(row.item_type?.replace("_", " ")),
      row.item_name || row.description || '',
      formatToPKR(row.total || 0),
      formatToPKR(row.discount || 0)
    ];

    // Calculate wrapped heights
    const wrappedData = rowData.map((cellValue, cellIndex) => {
      const text = cellValue?.toString() || '';
      const availableWidth = colWidths[cellIndex] - 4; // Including padding
      return pdf.splitTextToSize(text, availableWidth);
    });

    const maxLines = Math.max(...wrappedData.map(lines => lines.length));
    const dynamicRowHeight = (maxLines * lineHeight) + (paddingY * 2);

    if (yPosition + dynamicRowHeight > pageHeight - margin) {
      pdf.addPage();
      yPosition = margin;
      drawHeader(yPosition);
      yPosition += 8;
      pdf.setFontSize(7);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(15, 23, 42);
    }

    if (rowIndex % 2 === 0) {
      pdf.setFillColor(249, 250, 251);
      pdf.rect(tableStartX, yPosition, usableWidth, dynamicRowHeight, 'F');
    }

    let xPosition = tableStartX;
    wrappedData.forEach((lines, cellIndex) => {
      const paddingX = 2;
      
      lines.forEach((line: string, lineIndex: number) => {
        const textY = yPosition + paddingY + ((lineIndex + 0.8) * lineHeight);
        if (cellIndex >= 5) {
          pdf.text(line, xPosition + colWidths[cellIndex] - paddingX, textY, { align: 'right' });
        } else {
          pdf.text(line, xPosition + paddingX, textY);
        }
      });
      xPosition += colWidths[cellIndex];
    });

    pdf.setDrawColor(226, 232, 240);
    pdf.setLineWidth(0.2);
    pdf.line(tableStartX, yPosition + dynamicRowHeight, tableStartX + usableWidth, yPosition + dynamicRowHeight);
    yPosition += dynamicRowHeight;
  });

  pdf.save(filename);
}
